import { z } from 'zod';

// Post-0009: 'quote' is the new pre-acceptance status (replaces the old
// separate `deals` table). The flow is now Lead → Quote (project status='quote')
// → Active project → Completed/Cancelled.
export const PROJECT_STATUSES = ['quote', 'active', 'paused', 'completed', 'cancelled'] as const;
export type ProjectStatus = (typeof PROJECT_STATUSES)[number];

export const PROJECT_STATUS_LABELS_HE: Record<ProjectStatus, string> = {
  quote: 'הצעת מחיר',
  active: 'פעיל',
  paused: 'בהמתנה',
  completed: 'הושלם',
  cancelled: 'בוטל',
};

/**
 * The button text for moving a project from one status to another. Verb-first
 * per brand voice; "active" reads differently depending on where it comes from.
 */
export function projectTransitionLabel(from: ProjectStatus, to: ProjectStatus): string {
  if (to === 'active') {
    if (from === 'quote') return 'התחל פרויקט';
    if (from === 'paused') return 'חדש עבודה';
    return 'פתח מחדש';
  }
  if (to === 'paused') return 'השהה';
  if (to === 'completed') return 'סמן כהושלם';
  if (to === 'cancelled') return 'בטל פרויקט';
  return PROJECT_STATUS_LABELS_HE[to];
}

/** Label for a status string of unknown provenance (URL params, raw rows). */
export function projectStatusLabel(status: string): string {
  return (PROJECT_STATUS_LABELS_HE as Record<string, string | undefined>)[status] ?? status;
}

export const projectSchema = z.object({
  name: z.string().trim().min(1, 'שדה חובה').max(160, 'עד 160 תווים'),
  status: z.enum(PROJECT_STATUSES),
  company_id: z.string().uuid().optional().or(z.literal('')),
  product_line_id: z.string().uuid().optional().or(z.literal('')),
  value_amount: z.number().min(0).optional(),
  hourly_rate: z.number().min(0, 'לא יכול להיות שלילי').optional(),
  started_at: z.string().optional(),
  expected_end_date: z.string().optional(),
  notes: z.string().trim().max(4000, 'עד 4000 תווים').optional(),
});

export type ProjectInput = z.infer<typeof projectSchema>;

export interface ProjectRow {
  id: string;
  organization_id: string;
  name: string;
  status: ProjectStatus;
  company_id: string | null;
  product_line_id: string | null;
  quote_id: string | null;
  source_lead_id: string | null;
  primary_contact_id: string | null;
  value_amount: number;
  value_currency: string;
  hourly_rate: number | null;
  owned_by: string | null;
  started_at: string | null;
  expected_end_date: string | null;
  completed_at: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface ProjectCompanyOption {
  id: string;
  name: string;
}
